import React, { useState, useEffect } from 'react';
import { operatorAPI } from '../../lib/api';
import { 
  AlertTriangle, RefreshCw, Package, XCircle, Clock, 
  RotateCcw, CheckCircle, Mail
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Operator Queue - Failed/Unsent Deliveries
 * Endpoints used:
 * - GET /api/operator/queue/unsent-packs
 * - GET /api/operator/debug/failures
 * - POST /api/operator/retry/{delivery_id}
 */
export const OperatorQueue = () => {
  const [unsentPacks, setUnsentPacks] = useState([]);
  const [failures, setFailures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retrying, setRetrying] = useState({});
  const [activeTab, setActiveTab] = useState('unsent');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setError(null);
      setLoading(true);
      
      const [unsentRes, failuresRes] = await Promise.all([
        operatorAPI.getUnsent(100).catch(e => ({ data: { unsent_packs: [] } })),
        operatorAPI.getFailures(50).catch(e => ({ data: { failures: [] } })),
      ]);
      
      setUnsentPacks(unsentRes.data?.unsent_packs || []);
      setFailures(failuresRes.data?.failures || []);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async (deliveryId) => {
    try {
      setRetrying({ ...retrying, [deliveryId]: true });
      await operatorAPI.retryDelivery(deliveryId);
      toast.success('Retry scheduled');
      loadData();
    } catch (err) {
      toast.error('Retry failed: ' + err.message);
    } finally {
      setRetrying({ ...retrying, [deliveryId]: false });
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="queue-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="operator-queue-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Delivery Queue</h1>
          <p className="text-slate-500 mt-1">
            {unsentPacks.length} unsent packs, {failures.length} failures
          </p>
        </div>
        <button
          onClick={loadData}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50"
          data-testid="refresh-queue"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('unsent')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'unsent' 
              ? 'bg-amber-100 text-amber-800' 
              : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
          }`}
        >
          <Package className="w-4 h-4" />
          Unsent Packs ({unsentPacks.length})
        </button>
        <button
          onClick={() => setActiveTab('failures')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'failures' 
              ? 'bg-red-100 text-red-800' 
              : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
          }`}
        >
          <XCircle className="w-4 h-4" />
          Failures ({failures.length})
        </button>
      </div>

      {/* Unsent Packs */}
      {activeTab === 'unsent' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="unsent-packs-list">
          {unsentPacks.length === 0 ? (
            <div className="p-12 text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <h3 className="font-semibold text-slate-900">All Clear!</h3>
              <p className="text-sm text-slate-500">No unsent nanny packs in queue.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {unsentPacks.map((pack, idx) => (
                <div key={pack.id || idx} className="p-4 hover:bg-slate-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          pack.pack_type === 'email_summary' ? 'bg-blue-100 text-blue-700' :
                          pack.pack_type === 'task_reminder' ? 'bg-amber-100 text-amber-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {pack.pack_type}
                        </span>
                        <span className="text-sm font-medium text-slate-900">{pack.child_name}</span>
                      </div>
                      
                      {pack.content_preview && (
                        <p className="text-sm text-slate-500 line-clamp-2 mt-1">
                          {pack.content_preview}
                        </p>
                      )}
                      
                      {pack.error && (
                        <p className="text-sm text-red-600 mt-2 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          {pack.error.substring(0, 100)}...
                        </p>
                      )}
                      
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatTime(pack.created_at)}
                        </span>
                        <span className="font-mono">{pack.family_id?.slice(0, 8)}...</span>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => handleRetry(pack.id)}
                      disabled={retrying[pack.id]}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 disabled:opacity-50"
                    >
                      {retrying[pack.id] ? (
                        <RefreshCw className="w-3 h-3 animate-spin" />
                      ) : (
                        <RotateCcw className="w-3 h-3" />
                      )}
                      Retry
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Failures */}
      {activeTab === 'failures' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="failures-list">
          {failures.length === 0 ? (
            <div className="p-12 text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <h3 className="font-semibold text-slate-900">No Failures!</h3>
              <p className="text-sm text-slate-500">No recent delivery failures.</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Type</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Error</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Time</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Family</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {failures.map((failure, idx) => (
                  <tr key={failure.id || idx} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700">
                        {failure.delivery_type || 'unknown'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-slate-700 max-w-md truncate">
                        {failure.error_message || 'Unknown error'}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500">
                      {formatTime(failure.created_at)}
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-slate-400">
                      {failure.family_id?.slice(0, 8)}...
                    </td>
                    <td className="px-4 py-3">
                      {failure.id && (
                        <button
                          onClick={() => handleRetry(failure.id)}
                          disabled={retrying[failure.id]}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-700 rounded-lg text-sm hover:bg-slate-200 disabled:opacity-50"
                        >
                          {retrying[failure.id] ? (
                            <RefreshCw className="w-3 h-3 animate-spin" />
                          ) : (
                            <RotateCcw className="w-3 h-3" />
                          )}
                          Retry
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Info Box */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <Mail className="w-5 h-5 text-blue-500 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-800">About Twilio Rate Limits</h4>
            <p className="text-sm text-blue-700 mt-1">
              Twilio WhatsApp sandbox has a 50 msg/day limit. When exceeded (429 error), 
              nanny packs are still created and stored - only WhatsApp delivery fails.
              Use the retry buttons above once limit resets.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OperatorQueue;
